<template>
    <div class="portal-card-wrapper portal-card-wrapper--large">
        <div class="portal-card">
            <div class="review-card-header">
                <h2 class="review-heading">
                    <common-icon name="file" size="small" class="header-file-icon"></common-icon>
                    <span>{{ $t('pages.public.record_upload.step_review.title') }}</span>
                </h2>
                <span class="urgent-badge">
                    {{ $t('pages.public.record_upload.step_review.urgent_badge') }}
                </span>
            </div>

            <div class="portal-card__body review-body">
                <div class="review-section">
                    <p class="section-label">
                        {{ $t('pages.public.record_upload.step_review.requesting_provider') }}
                    </p>
                    <div class="section-box provider-box">
                        <p class="provider-name">{{ requestingProviderName }}</p>
                        <p class="provider-npi">{{ formattedNpi }}</p>
                    </div>
                </div>

                <div class="review-section">
                    <p class="section-label">
                        {{ $t('pages.public.record_upload.step_review.patient_identity') }}
                    </p>
                    <div class="section-box patient-grid">
                        <div class="patient-col">
                            <p class="sub-label">
                                {{ $t('pages.public.record_upload.step_review.full_name') }}
                            </p>
                            <p class="val-text">{{ patientName }}</p>
                        </div>
                        <div class="patient-col">
                            <p class="sub-label">
                                {{ $t('pages.public.record_upload.step_review.dob') }}
                            </p>
                            <p class="val-text">{{ dateOfBirth }}</p>
                        </div>
                        <div class="patient-col">
                            <p class="sub-label">
                                {{ $t('pages.public.record_upload.step_review.ssn_last4') }}
                            </p>
                            <div class="ssn-row">
                                <p class="ssn-val">{{ displayedSsn }}</p>
                                <button type="button" class="ssn-toggle-btn" @click="showSsn = !showSsn">
                                    <common-icon :name="showSsn ? 'eye-off' : 'eye'" size="extra-small"></common-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="review-section">
                    <p class="section-label">
                        {{ $t('pages.public.record_upload.step_review.records_requested') }}
                    </p>
                    <div class="section-box records-box">
                        <div class="records-pill-group">
                            <span v-for="rec in recordTypes" :key="rec" class="record-pill">
                                {{ rec }}
                            </span>
                        </div>
                        <div class="meta-row">
                            <common-icon name="timeline" size="extra-small" class="meta-icon"></common-icon>
                            <span>
                                {{ $t('pages.public.record_upload.step_review.date_range_label') }}
                                <strong class="meta-strong">{{ dateRangeText }}</strong>
                            </span>
                        </div>
                        <div class="meta-row">
                            <common-icon name="emergancy" size="extra-small" class="meta-icon"></common-icon>
                            <span>
                                {{ $t('pages.public.record_upload.step_review.purpose_label') }}
                                <strong class="meta-strong">{{ purpose }}</strong>
                            </span>
                        </div>
                    </div>
                </div>

                <div class="review-section">
                    <p class="section-label">
                        {{ $t('pages.public.record_upload.step_review.authorization') }}
                    </p>
                    <div class="auth-card">
                        <div class="auth-top">
                            <div class="auth-info">
                                <common-icon name="insure" size="small" class="green-shield-icon"></common-icon>
                                <div class="auth-text-group">
                                    <p class="auth-title">
                                        {{ $t('pages.public.record_upload.step_review.hipaa_attached') }}
                                    </p>
                                    <p class="auth-sub">
                                        {{ $t('pages.public.record_upload.step_review.hipaa_sub') }}
                                    </p>
                                </div>
                            </div>
                            <button type="button" class="btn-view-pdf" :disabled="isLoadingPdf" @click="viewPdf">
                                <common-icon name="eye" size="extra-small"></common-icon>
                                <span>{{ $t('pages.public.record_upload.step_review.view_pdf') }}</span>
                            </button>
                        </div>

                        <div class="auth-bottom">
                            <common-icon name="lock" size="extra-small" class="auth-lock-icon"></common-icon>
                            <p class="auth-bottom-text">
                                {{ $t('pages.public.record_upload.step_review.access_granted_prefix') }}
                                <span class="token-code">{{ tokenReference }}</span>
                                ·
                                {{ $t('pages.public.record_upload.step_review.access_granted_suffix') }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="response-requested-box">
                    <common-icon name="timer" size="small" class="clock-icon"></common-icon>
                    <span>
                        {{ $t('pages.public.record_upload.step_review.response_requested_by') }}
                        <strong>{{ responseDueDate }}</strong>
                    </span>
                </div>
            </div>
        </div>

        <div class="step-nav-bar">
            <button type="button" class="nav-btn-back" @click="$emit('back')">
                <common-icon name="left-arrow" size="small"></common-icon>
                <span>{{ $t('common.microcopy.back') }}</span>
            </button>
            <button type="button" class="nav-btn-action nav-btn-action--active" @click="$emit('proceed')">
                <span>{{ $t('pages.public.record_upload.step_review.proceed_upload') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
    import PatientRecordsAPI from 'api/PatientRecordsAPI'

    export default {
        name: 'step-review',
        props: {
            requestingProviderName: { type: String, default: 'Downtown Medical Associates' },
            npiNumber: { type: String, default: '1234567890' },
            patientName: { type: String, default: 'Amos David' },
            dateOfBirth: { type: String, default: '6/18/1987' },
            ssnLast4: { type: String, default: '1234' },
            recordTypes: {
                type: Array,
                default: () => ['Complete Medical History', 'Lab Results'],
            },
            dateRangeText: { type: String, default: '1/1/2023 – 1/1/2025' },
            purpose: { type: String, default: 'Specialist Referral' },
            tokenReference: { type: String, default: 'MMN-7X2K-9P4R' },
            responseDueDate: { type: String, default: '2/17/2025' },
            token: { type: String, default: '' },
        },
        data() {
            return {
                showSsn: false,
                isLoadingPdf: false,
            }
        },
        computed: {
            displayedSsn() {
                return this.showSsn ? this.ssnLast4 : '••••'
            },
            formattedNpi() {
                return this.$t('pages.public.record_upload.step_review.npi_prefix', {
                    npi: this.npiNumber,
                })
            },
        },
        methods: {
            viewPdf() {
                if (!this.token) {
                    this.$toast?.info?.('HIPAA Authorization Document preview available upon active session.')
                    return
                }
                this.isLoadingPdf = true
                PatientRecordsAPI.getUploadRequestHipaaForm(this.token)
                    .then((response) => {
                        const blob = new Blob([response.data], { type: 'application/pdf' })
                        const fileURL = URL.createObjectURL(blob)
                        window.open(fileURL, '_blank')
                    })
                    .catch(() => {
                        this.$toast?.error?.('Unable to load HIPAA Authorization document.')
                    })
                    .finally(() => {
                        this.isLoadingPdf = false
                    })
            },
        },
    }
</script>

<style scoped lang="scss">
    $brand-primary: #155dfc;
    $brand-primary-hover: #1048c7;
    $brand-border: #e2e8f0;

    .portal-card-wrapper {
        width: 100%;
    }

    .portal-card {
        background: #ffffff;
        border-radius: 16px;
        border: 1px solid $brand-border;
        box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
        overflow: hidden;

        &__body {
            padding: 24px;
        }
    }

    .review-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 1px solid #f1f5f9;
    }

    .review-heading {
        font-size: 18px;
        font-weight: 700;
        color: #111827;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .header-file-icon {
        color: #2563eb;
        display: inline-flex;
        align-items: center;
    }

    .urgent-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        padding: 2px 8px;
        font-size: 12px;
        font-weight: 500;
        text-transform: capitalize;
        background-color: #fef9c3;
        color: #854d0e;
        border: 1px solid #fef08a;
    }

    .review-body {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .review-section {
        display: flex;
        flex-direction: column;
    }

    .section-label {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #9ca3af;
        margin: 0 0 8px 0;
    }

    .section-box {
        background: #f9fafb;
        border-radius: 12px;
        padding: 16px;
    }

    .provider-box {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .provider-name {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        margin: 0;
    }

    .provider-npi {
        font-size: 14px;
        color: #6b7280;
        margin: 0;
    }

    .patient-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    .patient-col {
        display: flex;
        flex-direction: column;
    }

    .sub-label {
        font-size: 12px;
        color: #6b7280;
        margin: 0 0 2px 0;
    }

    .val-text {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        margin: 0;
    }

    .ssn-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ssn-val {
        font-size: 14px;
        font-weight: 600;
        font-family: monospace;
        color: #111827;
        margin: 0;
    }

    .ssn-toggle-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        color: #9ca3af;
        display: flex;
        align-items: center;
        transition: color 0.15s ease;

        &:hover {
            color: #4b5563;
        }
    }

    .records-box {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .records-pill-group {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .record-pill {
        background-color: #dbeafe;
        color: #1e40af;
        font-size: 12px;
        font-weight: 500;
        padding: 4px 10px;
        border-radius: 9999px;
    }

    .meta-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #4b5563;
    }

    .meta-icon {
        color: #9ca3af;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
    }

    .meta-strong {
        color: #1f2937;
        font-weight: 600;
    }

    /* Authorization Card */
    .auth-card {
        border: 1px solid #bbf7d0;
        border-radius: 12px;
        overflow: hidden;
    }

    .auth-top {
        background-color: #f0fdf4;
        padding: 12px 16px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }

    .auth-info {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }

    .green-shield-icon {
        color: #16a34a;
        margin-top: 2px;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
    }

    .auth-text-group {
        flex: 1;
        min-width: 0;
        font-size: 14px;
    }

    .auth-title {
        font-size: 14px;
        font-weight: 600;
        color: #166534;
        margin: 0;
    }

    .auth-sub {
        font-size: 12px;
        color: #15803d;
        margin: 2px 0 0 0;
    }

    .btn-view-pdf {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 500;
        color: #15803d;
        border: 1px solid #86efac;
        background-color: #ffffff;
        border-radius: 8px;
        padding: 6px 12px;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: #f0fdf4;
        }
    }

    .auth-bottom {
        background-color: #ffffff;
        border-top: 1px solid #dcfce7;
        padding: 10px 16px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .auth-lock-icon {
        color: #9ca3af;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
    }

    .auth-bottom-text {
        font-size: 12px;
        color: #6b7280;
        margin: 0;
    }

    .token-code {
        font-family: monospace;
        font-weight: 700;
        color: #374151;
    }

    /* Response Requested Banner */
    .response-requested-box {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #854d0e;
        background-color: #fefce8;
        border: 1px solid #fef08a;
        border-radius: 12px;
        padding: 12px 16px;

        strong {
            font-weight: 700;
        }
    }

    .clock-icon {
        color: #ca8a04;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
    }

    /* Bottom Navigation */
    .step-nav-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 20px;
    }

    .nav-btn-back {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 10px 18px;
        font-size: 14px;
        font-weight: 600;
        color: #0f172a;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: background-color 0.15s ease;

        &:hover {
            background: #f1f5f9;
        }
    }

    .nav-btn-action {
        flex: 1;
        border-radius: 8px;
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;

        &--active {
            background-color: $brand-primary;
            color: #ffffff;

            &:hover {
                background-color: $brand-primary-hover;
            }
        }
    }

    @media (max-width: 640px) {
        .patient-grid {
            grid-template-columns: 1fr;
        }
        .auth-top {
            flex-direction: column;
            align-items: flex-start;
        }
    }
</style>
